import { PropertyAttributeTypeId, PropertySchemaType } from "~/common/types";

import type { PropertySchemaAssociationFormData } from "~/features/admin/propertySchemas/types";
import { buildNewAssociation } from "./buildNewAssociation";

/**
 * For new entity-type categories, three property schemas are required by the
 * backend: a Title (String), a Date (Gdtf), and an EndDate (Gdtf).
 */
export const MANDATORY_PROPERTY_MAPPINGS = {
  [PropertyAttributeTypeId.Title]: PropertySchemaType.String,
  [PropertyAttributeTypeId.Date]: PropertySchemaType.Gdtf,
  [PropertyAttributeTypeId.EndDate]: PropertySchemaType.Gdtf,
} as const;

/** What the form needs to know about a schema inherited from an ancestor. */
export interface InheritedSchemaSummary {
  propertyTypeId: string;
  propertyAttributeTypeId: PropertyAttributeTypeId | null;
}

/** Inherited coverage needs the same attribute/type pair the own-row check requires. */
function isCoveredByInheritance(
  attributeType: PropertyAttributeTypeId,
  propertyTypeId: PropertySchemaType,
  inherited: readonly InheritedSchemaSummary[],
): boolean {
  return inherited.some((s) => s.propertyAttributeTypeId === attributeType && s.propertyTypeId === propertyTypeId);
}

/**
 * Mandatory attribute types not yet covered by the given list. A schema
 * inherited from an ancestor counts as coverage, so a child of a category that
 * already defines Title/Date/EndDate does not have to redeclare them.
 */
function listMissingMandatoryAttributeTypes(
  associations: readonly PropertySchemaAssociationFormData[],
  inherited: readonly InheritedSchemaSummary[] = [],
): PropertyAttributeTypeId[] {
  return Object.entries(MANDATORY_PROPERTY_MAPPINGS).flatMap(([attributeTypeId, propertyTypeId]) => {
    // Safe: Object.entries widens the key to string, but MANDATORY_PROPERTY_MAPPINGS is keyed by PropertyAttributeTypeId.
    const attributeType = attributeTypeId as PropertyAttributeTypeId;
    if (isCoveredByInheritance(attributeType, propertyTypeId, inherited)) return [];
    const own = associations.some(
      (a) => a.propertyAttributeTypeId === attributeType && a.propertySchema?.propertyTypeId === propertyTypeId,
    );
    return own ? [] : [attributeType];
  });
}

export function hasRequiredPropertySchemas(
  associations: readonly PropertySchemaAssociationFormData[],
  inherited: readonly InheritedSchemaSummary[] = [],
): boolean {
  return listMissingMandatoryAttributeTypes(associations, inherited).length === 0;
}

/**
 * Entity-create with a parent: drop only the pristine seeded rows (unsaved
 * mandatory rows the user never named) that the parent now provides, keep
 * everything the user has touched, and seed the mandatory rows still missing.
 * A kept row that shadows an inherited key is surfaced by the conflict check
 * instead of being discarded silently.
 */
export function reconcileMandatoryAssociations(
  current: readonly PropertySchemaAssociationFormData[],
  inherited: readonly InheritedSchemaSummary[],
): PropertySchemaAssociationFormData[] {
  const kept = current.filter((row) => {
    const attributeType = row.propertyAttributeTypeId;
    if (!attributeType || row.name) return true;
    if (!isMandatoryUnsavedSchema(attributeType, row.propertySchema.propertyTypeId, row.id)) return true;
    return !isCoveredByInheritance(attributeType, row.propertySchema.propertyTypeId, inherited);
  });
  const missing = listMissingMandatoryAttributeTypes(kept, inherited).map((attributeType, i) =>
    buildNewAssociation(attributeType, MANDATORY_PROPERTY_MAPPINGS[attributeType], kept.length + i),
  );
  return [...kept, ...missing];
}

/** A row whose attribute/type pair is in MANDATORY_PROPERTY_MAPPINGS *and* has no id (i.e., not yet saved). */
export function isMandatoryUnsavedSchema(
  attributeTypeId: PropertyAttributeTypeId | null | undefined,
  propertyTypeId: PropertySchemaType,
  id: string | undefined,
): boolean {
  if (id) return false;
  if (!attributeTypeId) return false;
  return MANDATORY_PROPERTY_MAPPINGS[attributeTypeId] === propertyTypeId;
}
